'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import BrandStory from '@/components/BrandStory';
import Modal from '@/components/Modal';
import { useModal } from '@/components/useModal';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';
import AgreementDrawer from '@/components/AgreementDrawer';
import SiteBeianLinks from '@/components/SiteBeianLinks';
import InviteContextBanner from '@/components/album/InviteContextBanner';
import { validatePassword } from '@/lib/utils';
import Link from 'next/link';
import { getBrandHomeUrl, getCreatorLoginUrl } from '@/lib/creatorPlatformUrls';
import { getInviteTokenFromSearch, getPostAuthRedirect, appendQueryParams } from '@/lib/postAuthRedirect';
import LoadingSpinner from '@/components/LoadingSpinner';

function RegisterForm() {
  const searchParams = useSearchParams();
  const inviteToken = getInviteTokenFromSearch(searchParams);
  const redirectPath = getPostAuthRedirect(searchParams);
  const prefilledEmail = searchParams.get('email')?.trim() || '';

  const [email, setEmail] = useState(prefilledEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreementDrawerOpen, setAgreementDrawerOpen] = useState(false);
  const [agreementType, setAgreementType] = useState<'terms-of-service' | 'privacy-policy'>('terms-of-service');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const router = useRouter();
  const { modalState, closeModal, showSuccess } = useModal();

  useEffect(() => {
    if (prefilledEmail) setEmail(prefilledEmail);
  }, [prefilledEmail]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('请先阅读并同意服务条款和隐私政策');
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError('密码强度不够，请确保密码包含大小写字母、数字和特殊字符');
      return;
    }

    if (password !== confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, 'creator', agreedToTerms, agreedToTerms, {
        inviteToken
      });

      const loginUrl = appendQueryParams(getCreatorLoginUrl(), {
        email,
        invite: inviteToken,
        redirect: redirectPath !== '/dashboard' ? redirectPath : undefined
      });

      showSuccess(
        '注册成功！验证邮件已发送至您的邮箱，请点击邮件中的链接完成验证后再登录。',
        undefined,
        3000
      );
      setTimeout(() => router.push(loginUrl), 3000);
    } catch (error: any) {
      setError(error.message || '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleTermsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAgreedToTerms(e.target.checked);
  };

  return (
    <main className="mobile-layout">
      <div className="mobile-left">
        <div className="mobile-content">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '3rem'
          }}>
            <Link href={getBrandHomeUrl()} style={{ textDecoration: 'none' }}>
              <img
                src="/logo.png"
                alt="基音"
                loading="eager"
                fetchPriority="high"
                style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.8';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              />
            </Link>
            <div style={{ textAlign: 'right' }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '600',
                color: '#000000',
                lineHeight: '1.2',
                marginBottom: '2px',
                letterSpacing: '0.3px'
              }}>基音</div>
              <div style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#000000',
                letterSpacing: '1px',
                textTransform: 'uppercase'
              }}>FUNDAMUSE</div>
            </div>
          </div>

          <h1 className="mobile-title">创建账户</h1>
          <p className="mobile-subtitle">加入基音，开启您的音乐创作之旅</p>

          <InviteContextBanner inviteToken={inviteToken} />

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label className="form-label">邮箱地址</label>
              <input
                type="email"
                placeholder="请输入邮箱地址"
                className="input-field mobile-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={!!prefilledEmail && !!inviteToken}
              />
            </div>

            <div className="form-group">
              <label className="form-label">密码</label>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="请输入密码"
                  className="input-field mobile-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? '隐藏密码' : '显示密码'}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            <div className="form-group">
              <label className="form-label">确认密码</label>
              <div className="password-container">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="请再次输入密码"
                  className="input-field mobile-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={toggleConfirmPasswordVisibility}
                  aria-label={showConfirmPassword ? '隐藏确认密码' : '显示确认密码'}
                >
                  {showConfirmPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div style={{
                color: '#ff4444',
                fontSize: '14px',
                marginBottom: '1rem',
                padding: '0.5rem',
                backgroundColor: '#fff5f5',
                borderRadius: '4px',
                border: '1px solid #fed7d7'
              }}>
                {error}
              </div>
            )}

            <div className="terms-agreement">
              <label className="terms-checkbox-label">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={agreedToTerms}
                  onChange={handleTermsChange}
                />
                <span className="terms-checkbox-text">
                  我已阅读并同意{' '}
                  <a
                    href="#"
                    className="link-hover"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAgreementType('terms-of-service');
                      setAgreementDrawerOpen(true);
                    }}
                  >
                    服务条款
                  </a>
                  {' '}和{' '}
                  <a
                    href="#"
                    className="link-hover"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setAgreementType('privacy-policy');
                      setAgreementDrawerOpen(true);
                    }}
                  >
                    隐私政策
                  </a>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="login-button mobile-button"
              disabled={loading || !agreedToTerms}
            >
              {loading ? '注册中...' : '创建账户'}
            </button>
          </form>

          <div className="register-guide">
            <p className="register-text">
              已有账户？{' '}
              <a
                href={appendQueryParams(getCreatorLoginUrl(), {
                  invite: inviteToken,
                  email,
                  redirect: redirectPath !== '/dashboard' ? redirectPath : undefined
                })}
                className="link-hover"
              >
                立即登录
              </a>
            </p>
          </div>

          <SiteBeianLinks className="auth-beian-links site-beian-links" />
        </div>
        <AgreementDrawer
          isOpen={agreementDrawerOpen}
          onClose={() => setAgreementDrawerOpen(false)}
          agreementType={agreementType}
        />
      </div>

      <div className="mobile-divider" />
      <BrandStory />

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        type={modalState.type}
        message={modalState.message}
      />
    </main>
  );
}

export default function Register() {
  return (
    <Suspense fallback={
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem'
      }}>
        <LoadingSpinner size="large" showText={false} />
      </div>
    }>
      <RegisterForm />
    </Suspense>
  );
}
